import { postRaw } from '../../../../common/axiosClient';
import { PdfImageQuality, PdfSizeInfo } from './pdfCompression';

export interface PdfExportResult {
  blob: Blob;
  /** Size breakdown from the PDF service, used to estimate other quality presets. */
  sizeInfo: PdfSizeInfo;
}

/**
 * Shared POST for the magic-report and limited-report /export endpoints.
 * Reads the X-Pdf-* size headers, falling back to the blob's own size when they
 * are absent (an older PDF service, or a proxy that strips them).
 */
export const requestPdfExport = async (
  endpoint: string,
  html: string,
  addBorder: boolean,
  imageQuality?: PdfImageQuality,
): Promise<PdfExportResult> => {
  const response = await postRaw(endpoint, JSON.stringify({ html, addBorder, imageQuality }), {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
    },
    responseType: 'blob',
  });

  const blob = response.data as Blob;
  const header = (name: string): string | undefined => response.headers?.[name.toLowerCase()];
  const numeric = (name: string, fallback: number): number => {
    const parsed = Number(header(name));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  };

  return {
    blob,
    sizeInfo: {
      totalBytes: numeric('X-Pdf-Bytes-Total', blob.size),
      imageBytes: numeric('X-Pdf-Bytes-Images', 0),
      imageCount: numeric('X-Pdf-Image-Count', 0),
      imageQuality: header('X-Pdf-Image-Quality') ?? imageQuality ?? 'original',
    },
  };
};
